import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
} from "@mui/material";
import { keyframes } from "@mui/system";
import { Phone, Email, AccessTime, Public } from "@mui/icons-material";

import contact1 from "../../pictures/contact1.jpg";
import OfficeMap from "../OfficeMap";

const float1 = keyframes`
  0% { transform: translateY(0px) translateX(0px) scale(1); }
  50% { transform: translateY(-18px) translateX(12px) scale(1.06); }
  100% { transform: translateY(0px) translateX(0px) scale(1); }
`;

const float2 = keyframes`
  0% { transform: translateY(0px) translateX(0px) scale(1); }
  50% { transform: translateY(14px) translateX(-10px) scale(1.08); }
  100% { transform: translateY(0px) translateX(0px) scale(1); }
`;

const shine = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

function ContactUs() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    location: "",
    landarea: "",
    subject: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [responseMsg, setResponseMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const submitcontact = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResponseMsg("");
    setErrorMsg("");

    try {
      const payload = new FormData();
      Object.entries(formData).forEach(([k, v]) => payload.append(k, v));

      const response = await fetch(
        "http://localhost/backend/api/contactSubmit.php",
        {
          method: "POST",
          body: payload,
        },
      );

      const data = await response.json();

      if (data.status) {
        setResponseMsg(data.message || "Message sent successfully!");
        setFormData({
          name: "",
          phone: "",
          email: "",
          location: "",
          landarea: "",
          subject: "",
          message: "",
        });
      } else {
        setErrorMsg(data.message || "Something went wrong!");
      }
    } catch (error) {
      setErrorMsg("Server error. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const FloatingDecor = () => (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
        zIndex: 0,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          width: 320,
          height: 320,
          borderRadius: "50%",
          top: "10%",
          left: "6%",
          background:
            "radial-gradient(circle, rgba(58,47,181,0.18), rgba(58,47,181,0.02) 70%)",
          filter: "blur(8px)",
          animation: `${float1} 10s ease-in-out infinite`,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: 260,
          height: 260,
          borderRadius: "50%",
          bottom: "12%",
          right: "7%",
          background:
            "radial-gradient(circle, rgba(245,124,0,0.16), rgba(245,124,0,0.02) 70%)",
          filter: "blur(10px)",
          animation: `${float2} 12s ease-in-out infinite`,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "18%",
          right: "18%",
          width: 150,
          height: 150,
          borderRadius: "32px",
          border: "1px solid rgba(255,255,255,0.35)",
          backdropFilter: "blur(18px)",
          background: "rgba(255,255,255,0.10)",
          boxShadow: "0 20px 50px rgba(13,6,81,0.10)",
          transform: "rotate(12deg)",
          animation: `${float1} 7s ease-in-out infinite`,
          display: { xs: "none", md: "block" },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "18%",
          left: "16%",
          width: 120,
          height: 120,
          borderRadius: "50%",
          border: "1px solid rgba(13,6,81,0.12)",
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.28), rgba(255,255,255,0.06))",
          backdropFilter: "blur(12px)",
          animation: `${float2} 9s ease-in-out infinite`,
          display: { xs: "none", md: "block" },
        }}
      />
    </Box>
  );

  return (
    <Box
      sx={{
        background:
          "linear-gradient(180deg, #f7f8ff 0%, #eef2ff 45%, #ffffff 100%)",
      }}
    >
      <Box
        sx={{
          position: "relative",
          minHeight: { xs: 340, md: 460 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          backgroundImage: `linear-gradient(rgba(8, 10, 28, 0.58), rgba(8, 10, 28, 0.74)), url(${contact1})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(circle at 20% 20%, rgba(58,47,181,0.28), transparent 25%), radial-gradient(circle at 80% 80%, rgba(245,124,0,0.22), transparent 28%)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 260,
            height: 260,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(255,255,255,0.22), rgba(255,255,255,0.02))",
            top: "8%",
            left: "6%",
            filter: "blur(10px)",
            animation: `${float1} 8s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 220,
            height: 220,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(79,109,255,0.30), rgba(79,109,255,0.03))",
            bottom: "8%",
            right: "8%",
            filter: "blur(12px)",
            animation: `${float2} 10s ease-in-out infinite`,
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            textAlign: "center",
            px: { xs: 3, md: 6 },
            py: { xs: 3, md: 4 },
            borderRadius: 5,
            background: "rgba(255,255,255,0.10)",
            backdropFilter: "blur(18px)",
            border: "1px solid rgba(255,255,255,0.18)",
            boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
            maxWidth: 760,
          }}
        >
          <Typography
            variant="h3"
            fontWeight={900}
            sx={{
              fontSize: { xs: "2rem", md: "3.4rem" },
              letterSpacing: "0.03em",
              textTransform: "uppercase",
              color: "#fff",
            }}
          >
            Get A Free Quote
          </Typography>

          <Typography
            sx={{
              mt: 1.5,
              fontSize: { xs: "0.95rem", md: "1.08rem" },
              color: "rgba(255,255,255,0.92)",
              maxWidth: 650,
              mx: "auto",
              lineHeight: 1.8,
            }}
          >
            Our construction team is ready to help you plan, design, and build
            with quality and transparency.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          position: "relative",
          py: { xs: 5, md: 9 },
          background:
            "linear-gradient(135deg, #f7f8ff 0%, #eef2ff 40%, #ffffff 100%)",
          overflow: "hidden",
        }}
      >
        <FloatingDecor />

        <Container sx={{ position: "relative", zIndex: 2 }}>
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 3.5,
                  borderRadius: 5,
                  height: "100%",
                  background:
                    "linear-gradient(145deg, rgba(255,255,255,0.88), rgba(248,250,255,0.72))",
                  backdropFilter: "blur(16px)",
                  border: "1px solid rgba(13,6,81,0.08)",
                  boxShadow: "0 18px 46px rgba(13,6,81,0.09)",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    background:
                      "radial-gradient(circle at top right, rgba(58,47,181,0.10), transparent 35%)",
                    pointerEvents: "none",
                  },
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight={800}
                  gutterBottom
                  sx={{ color: "#0d0651" }}
                >
                  Office Address
                </Typography>

                <Typography sx={{ color: "#556", mb: 3 }}>
                  Reach us at our corporate location or contact us directly for
                  your project requirements.
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    mb: 3,
                    alignItems: "flex-start",
                  }}
                >
                  <Public sx={{ color: "#0d0651", mt: 0.4 }} />
                  <Typography sx={{ color: "#333", lineHeight: 1.7 }}>
                    <b>Corporate Location</b>
                    <br />
                    Chennai (Mogappair), Pincode: 600053
                    <br />
                    Distt. Thiruvallur (Tamil Nadu)
                  </Typography>
                </Box>

                <Box
                  sx={{ display: "flex", gap: 2, mb: 3, alignItems: "center" }}
                >
                  <Phone sx={{ color: "#0d0651" }} />
                  <Typography sx={{ color: "#333" }}>+91 9840271137</Typography>
                </Box>

                <Box
                  sx={{ display: "flex", gap: 2, mb: 3, alignItems: "center" }}
                >
                  <Email sx={{ color: "#0d0651" }} />
                  <Typography
                    component="a"
                    href="mailto:velvizhiconstruction@gmail.com"
                    sx={{
                      color: "#0d0651",
                      textDecoration: "none",
                      fontWeight: 600,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    velvizhiconstruction@gmail.com
                  </Typography>
                </Box>

                <Box
                  sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}
                >
                  <AccessTime sx={{ color: "#0d0651" }} />
                  <Typography sx={{ color: "#333" }}>
                    Monday - Friday: 9:00 AM - 5:00 PM
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <AccessTime sx={{ color: "#0d0651" }} />
                  <Typography sx={{ color: "#333" }}>
                    Saturday & Sunday Closed
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={8}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4.5 },
                  borderRadius: 5,
                  background:
                    "linear-gradient(145deg, rgba(255,255,255,0.90), rgba(248,250,255,0.78))",
                  backdropFilter: "blur(16px)",
                  border: "1px solid rgba(13,6,81,0.08)",
                  boxShadow: "0 18px 46px rgba(13,6,81,0.09)",
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight={800}
                  gutterBottom
                  sx={{ color: "#0d0651" }}
                >
                  Talk To Us - We're Here To Help
                </Typography>

                <Typography sx={{ color: "#566", mb: 3 }}>
                  Fill in the details below and our team will get back to you
                  soon.
                </Typography>

                {responseMsg && (
                  <Alert severity="success" sx={{ mb: 3 }}>
                    {responseMsg}
                  </Alert>
                )}

                {errorMsg && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {errorMsg}
                  </Alert>
                )}

                <Box component="form" onSubmit={submitcontact}>
                  <Grid container spacing={3}>
                    {[
                      { label: "Name", name: "name" },
                      { label: "Phone Number", name: "phone" },
                      { label: "Email Address", name: "email", type: "email" },
                      { label: "Location", name: "location" },
                      { label: "Land Area (sq.ft)", name: "landarea" },
                      { label: "Subject", name: "subject" },
                    ].map((field, idx) => (
                      <Grid
                        item
                        xs={12}
                        md={idx < 2 ? 6 : idx < 4 ? 6 : 12}
                        key={field.name}
                      >
                        <TextField
                          fullWidth
                          type={field.type || "text"}
                          label={field.label}
                          name={field.name}
                          value={formData[field.name]}
                          onChange={handleChange}
                          required
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2.5,
                              background: "#fff",
                              transition: "0.25s ease",
                              "&:hover fieldset": { borderColor: "#3a2fb5" },
                              "&.Mui-focused fieldset": {
                                borderColor: "#0d0651",
                                borderWidth: 2,
                              },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "#0d0651",
                            },
                          }}
                        />
                      </Grid>
                    ))}

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="How Can We Help?"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2.5,
                            background: "#fff",
                            "&:hover fieldset": { borderColor: "#3a2fb5" },
                            "&.Mui-focused fieldset": {
                              borderColor: "#0d0651",
                              borderWidth: 2,
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#0d0651",
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        size="large"
                        type="submit"
                        disabled={loading}
                        sx={{
                          minWidth: 200,
                          borderRadius: 999,
                          py: 1.5,
                          px: 4,
                          fontWeight: 800,
                          background:
                            "linear-gradient(90deg, #0d0651, #3a2fb5, #f57c00)",
                          backgroundSize: "200% 200%",
                          animation: `${shine} 6s ease infinite`,
                          boxShadow: "0 12px 28px rgba(13,6,81,0.28)",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 16px 32px rgba(13,6,81,0.34)",
                          },
                        }}
                      >
                        {loading ? (
                          <>
                            <CircularProgress
                              size={22}
                              sx={{ color: "#fff", mr: 1 }}
                            />
                            Submitting...
                          </>
                        ) : (
                          "Submit"
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ pb: 6 }}>
        <OfficeMap />
      </Box>
    </Box>
  );
}

export default ContactUs;
